import { yupResolver } from "@hookform/resolvers/yup";
import yup from "@shared/lib/yupExtensions/index";
import { FormProvider, useForm } from "react-hook-form";

import { usePublicBasket } from "@app-contexts/public/features/basket/services/usePublicBasket";
import { getCountryCodeFromCurrency } from "@shared/components/ui-kit/form/PhoneNumberInput/utils";
import {
  getCountryCodeFromPhoneInput,
  PhoneCountryType,
} from "@shared/lib/libphonenumber/api";
import { useTranslation } from "@shared/utils/internationalization";
import Button from "@ui-kit/form/Button/Button.web";
import Box from "@ui-kit/layout/Box/Box.web";
import { useEffect, useMemo, useState } from "react";
import SectionHeader from "@app-contexts/public/shared/checkout/components/steps/payment/components/SectionHeader.web";
import PurchaserInfoForm from "./Form/PurchaserInfoForm.web";
import { PurchaserInfoSharedProps } from "./PurchaserInfo.props";
import PurchaserInfoSummary from "./Summary/PurchaserInfoSummary.web";
import SubscribeCheckboxes from "./SubscribeCheckboxes/SubscribeCheckboxes.web";

function PurchaserInfo({
  nameOnTicket,
  email,
  phoneNumber,
  subscribeToCompany,
  subscribeToVenue,
  venueData,
  onSubmit,
  onValidationChange,
}: PurchaserInfoProps) {
  const { t } = useTranslation("checkout");
  const { currency, isGroupSale, holdLinkSlug, customerGroupId } =
    usePublicBasket();

  const isGroupSaleHold = useMemo(() => {
    return isGroupSale && Boolean(holdLinkSlug) && Boolean(customerGroupId);
  }, [isGroupSale, holdLinkSlug, customerGroupId]);

  const [phoneCountry, setPhoneCountry] = useState<PhoneCountryType>({
    countryCode: phoneNumber
      ? getCountryCodeFromPhoneInput(phoneNumber)
      : getCountryCodeFromCurrency(currency ?? ""),
  });

  const handlePhoneCountryChange = (phoneCountry: PhoneCountryType) => {
    setPhoneCountry(phoneCountry);
  };

  const form = useForm<{
    nameOnTicket: string;
    email: string;
    phoneNumber: string;
    subscribeToCompany: boolean;
    subscribeToVenue: boolean;
  }>({
    mode: "onBlur",
    shouldFocusError: false,
    defaultValues: {
      nameOnTicket: nameOnTicket || "",
      email: email || "",
      phoneNumber: phoneNumber || "",
      subscribeToCompany: subscribeToCompany ?? false,
      subscribeToVenue: subscribeToVenue ?? false,
    },
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    resolver: yupResolver(
      yup.object().shape({
        nameOnTicket: yup
          .string()
          .required(t("Full name is required"))
          .test(
            "has-first-and-last-name",
            t("Please enter both first and last name"),
            (value) => {
              if (!value) return false;
              const nameParts = value.trim().split(/\s+/);
              return (
                nameParts.length >= 2 &&
                nameParts.every((part) => part.length > 0)
              );
            }
          )
          .test(
            "no-special-characters",
            t(
              "Name can only contain letters, spaces, hyphens, and apostrophes"
            ),
            (value) => {
              if (!value) return true; // Let the required validation handle empty values
              // Allow letters, spaces, hyphens, and apostrophes
              const validNameRegex = /^[a-zA-Z\s\-']+$/;
              return validNameRegex.test(value);
            }
          ),
        email: yup
          .string()
          .required(t("Email is required"))
          .email(t("Email must be a valid email address")),
        phoneNumber: yup
          .string()
          .required(t("Phone number is required"))
          .phone(t("Invalid phone number for selected country"), phoneCountry),
        subscribeToCompany: yup.boolean().notRequired(),
        subscribeToVenue: yup.boolean().notRequired(),
      })
    ),
  });

  const [isEditing, setIsEditing] = useState(
    () =>
      // Default to editing mode if any of the purchaser info fields are missing
      !nameOnTicket || !email || !phoneNumber
  );

  useEffect(() => {
    // On editing mode, check if form is considered valid
    if (isEditing) {
      onValidationChange?.(form.formState.isValid);
      return;
    }

    // Otherwise, check if fields are truthy
    onValidationChange?.(Boolean(nameOnTicket && email && phoneNumber));
  }, [form.formState.isValid, isEditing]);

  return (
    <FormProvider {...form}>
      <form
        data-testid="purchaser-info-form"
        onBlur={form.handleSubmit(onSubmit)}
      >
        <Box>
          <Box>
            <SectionHeader
              sectionNumber={1}
              title={t("Purchaser information")}
              isValid={form.formState.isValid}
              cta={
                <Button.Link
                  data-testid="purchaser-info-button-edit"
                  visibility={
                    isEditing || isGroupSaleHold ? "hidden" : "visible"
                  }
                  colorPalette="tertiary"
                  onClick={() => {
                    setIsEditing(true);
                  }}
                >
                  {t("Edit")}
                </Button.Link>
              }
            />
          </Box>

          <Box m="8px 0px">
            {!isEditing && nameOnTicket && email ? (
              <PurchaserInfoSummary nameOnTicket={nameOnTicket} email={email} />
            ) : (
              <PurchaserInfoForm
                handlePhoneCountryChange={handlePhoneCountryChange}
              />
            )}
          </Box>

          <Box mt="md">
            <SubscribeCheckboxes venueData={venueData} />
          </Box>
        </Box>
      </form>
    </FormProvider>
  );
}

export default PurchaserInfo;

type PurchaserInfoProps = PurchaserInfoSharedProps;
